import type { Tables } from "@msuite/utils/types";
import type { Citadel } from "./citadel";
import type { FileParentSubject } from "./file-parents";
import type { ActionFor, AppSubjectName } from "./types";

/**
 * The yes-or-no questions a client asks before it renders a control — hiding a button, locking a
 * row, choosing which half of an editability rule applies. They are pure functions over a
 * resolved `Citadel` rather than hooks, so the web app and the native app ask the same question
 * of the same claims and cannot drift: a control that is hidden on one is hidden on the other,
 * and neither offers a write the server refuses.
 *
 * Every predicate here answers the **denial** side, and that is what makes the three states
 * distinguishable. `citadel` is undefined while the rules are still loading, and also when a
 * refetch failed with no cached rules left to answer from; neither is a refusal, so nothing is
 * denied and the control stays. Only a resolved claim set that refuses returns `true`. The `Can`
 * component (`apps/web/src/components/citadel/can.tsx`) documents the same guard for the surfaces
 * that render through it.
 */

/**
 * Whether the caller's rules definitively refuse one action on one subject.
 *
 * @param citadel - The caller's resolved claims, or undefined while they are unavailable.
 * @param action - The action the control would perform.
 * @param subject - The subject it would perform it on.
 * @param resource - The row being acted on, when the check is about a concrete record rather
 *   than the category. Omitted, a conditioned grant passes — the category question.
 * @returns True only on a definitive denial; false while `citadel` is undefined.
 */
export function isDenied<S extends AppSubjectName>(
	citadel: Citadel | undefined,
	action: ActionFor<S>,
	subject: S,
	resource?: Record<string, unknown>,
): boolean {
	return !!citadel && citadel.cannot(action, subject, resource);
}

/**
 * Which side of an editability rule the caller stands on: `"admin"` when their `update` on the
 * subject carries no condition — the owner and the admin role — and `"user"` for everybody whose
 * right is bound to their own rows.
 *
 * The server asks the same question of the same subject before it writes (`editorRole` in
 * `apps/server/src/handlers/users/absences.ts`, and its twin for attendances), and the answer
 * feeds the shared rule — `getIsAbsenceEditable`, `getIsAttendanceEditable` — on both sides. The
 * two must agree: a client that decides `"admin"` where the server decides `"user"` offers an
 * edit that comes back a 403, and the opposite hides a correction the caller is entitled to
 * make. While the rules are unavailable the answer is `"user"`, the narrower of the two.
 *
 * @param citadel - The caller's resolved claims, or undefined while they are unavailable.
 * @param subject - `"Absence"` or `"Attendance"`, the two subjects whose editability splits this
 *   way.
 * @returns `"admin"` for an unconditioned `update`, otherwise `"user"`.
 */
export function editorRoleFor(
	citadel: Citadel | undefined,
	subject: "Absence" | "Attendance",
): "admin" | "user" {
	return citadel?.isUnrestrictedFor("update", subject) ? "admin" : "user";
}

/**
 * The row the fixed ticket rule binds against. `projectId` is what it needs: ticket work belongs
 * to the project's members, their active substitutes, and admins — `Citadel.canEditTickets` —
 * and every surface that shows a ticket carries the project it sits on. `orgId`, where the
 * surface has it, refuses a ticket of another organisation outright, which the admin wildcard
 * would otherwise cover.
 */
export type TicketResource = Pick<Tables<"tickets">, "id" | "projectId"> & {
	orgId?: string | null;
};

/**
 * Whether the fixed ticket rule refuses this caller the ticket's project.
 *
 * No grant is asked: Ticket is not a subject, and the answer comes from the caller's
 * relationship to the project — membership (widened by active substitution windows in the
 * resolver's claim) or the admin wildcard.
 *
 * @param citadel - The caller's resolved claims.
 * @param ticket - The row as the surface has it.
 * @returns True only when the relationship definitively refuses.
 */
export function updateIsDeniedFor(citadel: Citadel, ticket: TicketResource) {
	return !citadel.canEditTickets(ticket.projectId, ticket.orgId);
}

/**
 * Whether the fixed ticket rule refuses this caller a new ticket in one project.
 *
 * A null project is not a denial: a project that does not exist yet has no members to be one of
 * — the new-project form queues its tickets before the site is created — so the question is
 * answered against the project once the server has it.
 *
 * @param citadel - The caller's resolved claims, or undefined while they are unavailable.
 * @param projectId - The project the ticket would belong to, or null when there is none yet.
 * @param projectOrgId - The project's organisation, where the surface has it.
 * @returns True only when the relationship definitively refuses a named project.
 */
export function createIsDeniedFor(
	citadel: Citadel | undefined,
	projectId: string | null,
	projectOrgId?: string | null,
): boolean {
	if (!citadel || !projectId) return false;
	return !citadel.canEditTickets(projectId, projectOrgId);
}

/**
 * Whether the caller is refused arranging one project's work on a board — putting a ticket on a
 * crew, reordering it, taking it off.
 *
 * Wider than `createIsDeniedFor` by one arm: arranging is `update Plan` — the board-management
 * right an organisation grants through the grid — **or** the project relationship, which is what
 * `Citadel.canArrangeTickets` answers. The ticket record itself stays with the narrower rule.
 *
 * @param citadel - The caller's resolved claims, or undefined while they are unavailable.
 * @param projectId - The project of the ticket being arranged.
 * @param projectOrgId - The project's organisation, where the surface has it.
 * @returns True only when neither arm covers the project.
 */
export function arrangeIsDeniedFor(
	citadel: Citadel | undefined,
	projectId: string,
	projectOrgId?: string | null,
): boolean {
	return !!citadel && !citadel.canArrangeTickets(projectId, projectOrgId);
}

/**
 * What a file hangs on, as the surface that shows it knows it — the question
 * `fileWriteIsDeniedFor` answers against.
 *
 * - `{ subject, resource }`: the parent row, loaded, in the shape that subject's conditions bind
 *   on (`orgId` for Stock, `orgId`/`createdBy` for a note, and so on — the census in `types.ts`).
 *   `Organization` is the parent of the two collections the files API does not address, the
 *   worksite documents and the workspace media; its resource is `{ id: orgId, orgId }`.
 * - `{ ticket }`: a ticket's files follow the fixed ticket rule, so the project id is all that
 *   is asked.
 * - `{ file }`: a file linked to nothing — an unfiled drop in the inbox, an orphan whose parent
 *   was deleted. Its uploader and the administrator may write it; nobody else holds a parent to
 *   derive a right from.
 */
export type FileParent =
	| {
			subject: Exclude<FileParentSubject, "Ticket"> | "Organization";
			resource: Record<string, unknown>;
	  }
	| { ticket: TicketResource }
	| { file: Pick<Tables<"files">, "createdBy"> };

/**
 * Whether the caller is refused writing a file's attachments — attaching to the parent,
 * replacing, unlinking, moving, annotating or removing what hangs there.
 *
 * File is not a subject, so no file rule is asked: the answer is `update` on the parent, the
 * ticket rule for a ticket, and uploader-or-administrator for a file that hangs on nothing. This
 * is the client half of `Files.authorizeParent` and `Files.authorizeFile` on the server, and the
 * two must agree for the same reason `editorRoleFor` must: a control that is shown and answered
 * 403 is worse than one that is hidden.
 *
 * @param citadel - The caller's resolved claims, or undefined while they are unavailable.
 * @param parent - What the file hangs on, or the file itself when it hangs on nothing.
 * @returns True only on a definitive refusal; false while `citadel` is undefined.
 */
export function fileWriteIsDeniedFor(citadel: Citadel | undefined, parent: FileParent): boolean {
	if (!citadel) return false;
	if ("ticket" in parent) {
		return !citadel.canEditTickets(parent.ticket.projectId, parent.ticket.orgId);
	}
	if ("file" in parent) {
		return !(citadel.can("manage", "all") || parent.file.createdBy === citadel.userId);
	}
	return citadel.cannot("update", parent.subject, parent.resource);
}
